import fs from 'node:fs/promises';
import os from 'node:os';
import path from 'node:path';
import process from 'node:process';
import { readBrandEnv } from './brand.js';
import { DEFAULT_API_URL, DEFAULT_APP_URL } from './default-urls.js';

export type StoredAuth = {
  token?: string;
  apiUrl?: string;
  orgId?: string;
  orgName?: string;
  /**
   * Which environment this session belongs to (`production` | `dev` | `local` |
   * custom). Purely for display/filtering — `apiUrl` already encodes the env —
   * so the same org can hold a session in more than one environment at once.
   */
  envLabel?: string;
};

/** Multi-profile auth file format */
type MultiProfileAuth = {
  profiles: Record<string, StoredAuth>;
  default: string;
};

/** Legacy flat format (single profile) */
type LegacyAuth = StoredAuth;

/**
 * Resolve where the multi-profile auth file lives. Honors a
 * `CANARY_CONFIG_DIR` / `COFACTOR_CONFIG_DIR` override (for non-standard config
 * locations and tests), else the default `~/.config/canary-cli`.
 */
function getAuthPaths(): { dir: string; file: string } {
  const dir = readBrandEnv('CANARY_CONFIG_DIR') ?? path.join(os.homedir(), '.config', 'canary-cli');
  return { dir, file: path.join(dir, 'auth.json') };
}

/* ── Shared argv helpers ─────────────────────────────────────────────── */

export function getArgValue(argv: string[], key: string): string | undefined {
  const index = argv.indexOf(key);
  if (index === -1 || index >= argv.length - 1) return undefined;
  return argv[index + 1];
}

export function hasFlag(argv: string[], ...flags: string[]): boolean {
  return flags.some((flag) => argv.includes(flag));
}

/* ── Environment / profile mapping ───────────────────────────────────── */

/** Map --env values to profile names for multi-profile auth storage */
export function envToProfile(env: string): string {
  if (env === 'prod' || env === 'production') return 'production';
  if (env === 'dev') return 'dev';
  if (env === 'local') return 'local';
  return env;
}

const VALID_ENVS = ['prod', 'dev', 'local'];

/** Environment URL mappings shared across CLI commands */
export const ENV_URLS: Record<string, { api: string; app: string }> = {
  prod: {
    api: DEFAULT_API_URL,
    app: DEFAULT_APP_URL,
  },
  production: {
    api: DEFAULT_API_URL,
    app: DEFAULT_APP_URL,
  },
  dev: {
    api: 'https://api.dev.trycanary.ai',
    app: 'https://app.dev.trycanary.ai',
  },
  local: {
    api: 'http://localhost:3000',
    app: 'http://localhost:5173',
  },
};

/** Reverse of `ENV_URLS`: the env label (`production`|`dev`|`local`) for an API
 * URL, or `undefined` for a custom URL. Used to tag stored sessions. */
export function envLabelForApiUrl(apiUrl: string): string | undefined {
  for (const [key, urls] of Object.entries(ENV_URLS)) {
    if (urls.api === apiUrl) return envToProfile(key);
  }
  return undefined;
}

/**
 * Storage key for an org's session, scoped by environment so the same org can
 * be logged into prod and dev at once without one clobbering the other
 * (`org-123@dev` vs `org-123@production`).
 */
export function orgProfileKey(orgId: string, envLabel?: string): string {
  return envLabel ? `${orgId}@${envLabel}` : orgId;
}

/** Derive the web-app URL that corresponds to a given API URL. */
export function resolveAppUrl(apiUrl: string): string {
  for (const env of Object.values(ENV_URLS)) {
    if (env.api === apiUrl) return env.app;
  }
  // Fallback for custom API URLs: replace "api." at protocol boundary or "/api" path
  return apiUrl.replace(/(:\/\/)api\./, '$1app.').replace(/:3000\b/, ':5173');
}

function isMultiProfile(data: unknown): data is MultiProfileAuth {
  return typeof data === 'object' && data !== null && 'profiles' in data;
}

/** Read the raw auth file and normalize to multi-profile format */
async function readAuthFile(): Promise<MultiProfileAuth | null> {
  try {
    const content = await fs.readFile(getAuthPaths().file, 'utf8');
    const data = JSON.parse(content) as unknown;

    if (isMultiProfile(data)) {
      return data;
    }

    // Legacy flat format — treat as the "default" profile
    const legacy = data as LegacyAuth;
    return {
      profiles: { default: legacy },
      default: 'default',
    };
  } catch {
    return null;
  }
}

export async function readStoredAuth(profile?: string): Promise<StoredAuth | null> {
  const auth = await readAuthFile();
  if (!auth) return null;

  const key = profile ?? auth.default;
  return auth.profiles[key] ?? null;
}

export async function readStoredToken(profile?: string): Promise<string | null> {
  const auth = await readStoredAuth(profile);
  return auth?.token ?? null;
}

export async function readStoredApiUrl(profile?: string): Promise<string | null> {
  const auth = await readStoredAuth(profile);
  return auth?.apiUrl ?? null;
}

/** Return all stored tokens across every profile */
export async function readAllStoredTokens(): Promise<string[]> {
  const auth = await readAuthFile();
  if (!auth) return [];
  return Object.values(auth.profiles)
    .map((p) => p.token)
    .filter((t): t is string => !!t);
}

export async function saveAuth(auth: StoredAuth, profile?: string): Promise<string> {
  const { dir: AUTH_DIR, file: AUTH_FILE } = getAuthPaths();
  await fs.mkdir(AUTH_DIR, { recursive: true, mode: 0o700 });

  // Read existing file (may be legacy or multi-profile)
  let existing: MultiProfileAuth;
  try {
    const content = await fs.readFile(AUTH_FILE, 'utf8');
    const data = JSON.parse(content) as unknown;

    if (isMultiProfile(data)) {
      existing = data;
    } else {
      // Migrate legacy flat format into profiles.default
      existing = {
        profiles: { default: data as LegacyAuth },
        default: 'default',
      };
    }
  } catch {
    existing = { profiles: {}, default: 'default' };
  }

  const key = profile ?? 'default';
  existing.profiles[key] = auth;

  // Set default to this profile if no default exists yet
  if (!existing.profiles[existing.default]) {
    existing.default = key;
  }

  await fs.writeFile(AUTH_FILE, JSON.stringify(existing, null, 2), {
    encoding: 'utf8',
    mode: 0o600,
  });
  // writeFile's mode only applies on creation; enforce 0600 on overwrite too.
  await fs.chmod(AUTH_FILE, 0o600).catch(() => {});
  return AUTH_FILE;
}

/* ── Multi-org session lookup ────────────────────────────────────────── */

export type StoredProfile = { key: string; auth: StoredAuth };

/** Enumerate every stored session (profile) plus the current default key. */
export async function readAllProfiles(): Promise<{
  profiles: StoredProfile[];
  default: string;
}> {
  const auth = await readAuthFile();
  if (!auth) return { profiles: [], default: 'default' };
  return {
    profiles: Object.entries(auth.profiles).map(([key, a]) => ({ key, auth: a })),
    default: auth.default,
  };
}

export type SessionMatch =
  | { kind: 'match'; key: string; auth: StoredAuth }
  | { kind: 'none' }
  | { kind: 'ambiguous'; candidates: StoredProfile[] };

/**
 * Find the stored session for an org, selected by `orgId` (exact) or `orgName`
 * (case-insensitive). When `env` is given it narrows to sessions in that
 * environment, so the same org can be logged in across prod/dev without clashing.
 * Returns `ambiguous` when a bare name matches sessions in multiple envs.
 */
export async function findSessionByOrg(selector: string, env?: string): Promise<SessionMatch> {
  const auth = await readAuthFile();
  if (!auth) return { kind: 'none' };

  const sel = selector.toLowerCase();
  const all = Object.entries(auth.profiles).map(([key, a]) => ({ key, auth: a }));

  // Exact orgId match is unambiguous — prefer it over name matches.
  const byId = all.filter(({ auth: a }) => a.orgId === selector);
  let candidates = byId.length > 0 ? byId : all.filter(({ auth: a }) => a.orgName?.toLowerCase() === sel);

  // When --env is given, restrict to sessions in that environment — ALWAYS, even
  // for a single candidate. Otherwise a lone prod session would resolve under
  // `--env dev` and its token would be sent to the dev host (fail-open). No
  // match in the requested env → fall through to `none` so the caller errors.
  if (env) {
    const wantEnv = envToProfile(env);
    const wantApi = ENV_URLS[env]?.api;
    candidates = candidates.filter(
      ({ auth: a }) => a.envLabel === wantEnv || (!!wantApi && a.apiUrl === wantApi)
    );
  }

  // Collapse duplicate profiles that point at the SAME session: login mirrors a
  // session under both an org-scoped key (`org@env`) and the env-name key (for
  // `--env` lookups), so identical entries must not read as an ambiguous
  // multi-env match. Comparison is by token — the JWT is the real identity — so a
  // future refactor must not "fix" this into trusting local orgName/orgId fields.
  // The org-scoped key sorts first (written first), so it survives as representative.
  if (candidates.length > 1) {
    const seen = new Map<string, StoredProfile>();
    for (const c of candidates) {
      const idKey = c.auth.token ?? c.key;
      if (!seen.has(idKey)) seen.set(idKey, c);
    }
    candidates = [...seen.values()];
  }

  if (candidates.length === 0) return { kind: 'none' };
  if (candidates.length === 1) return { kind: 'match', key: candidates[0].key, auth: candidates[0].auth };
  return { kind: 'ambiguous', candidates };
}

/** Point the persistent `default` at an existing profile key. */
export async function setDefaultProfile(key: string): Promise<void> {
  const auth = await readAuthFile();
  if (!auth || !auth.profiles[key]) {
    throw new Error(`No stored session for profile "${key}".`);
  }
  auth.default = key;
  const { dir: AUTH_DIR, file: AUTH_FILE } = getAuthPaths();
  await fs.mkdir(AUTH_DIR, { recursive: true, mode: 0o700 });
  await fs.writeFile(AUTH_FILE, JSON.stringify(auth, null, 2), {
    encoding: 'utf8',
    mode: 0o600,
  });
  // writeFile's mode only applies on creation; enforce 0600 on overwrite too.
  await fs.chmod(AUTH_FILE, 0o600).catch(() => {});
}

/* ── Shared config resolution ────────────────────────────────────────── */

type ResolvedConfig = { apiUrl: string; token: string };

/**
 * Which stored session an invocation targets, before falling back to `--env`
 * profile / default. Returns the org selector string when one is in play (from
 * `--org` or `CANARY_ORG`), else `null`. `--env` takes precedence over the
 * `CANARY_ORG` env var (an explicit `--env` on the command line wins).
 */
export function getOrgSelector(argv: string[], env: string | undefined): string | null {
  const flag = getArgValue(argv, '--org');
  if (flag) return flag;
  if (env) return null;
  return readBrandEnv('CANARY_ORG') ?? null;
}

async function resolveConfigInternal(
  argv: string[],
  opts: { required: boolean }
): Promise<ResolvedConfig | null> {
  const env = getArgValue(argv, '--env');
  const profile = env ? envToProfile(env) : undefined;

  if (env && !ENV_URLS[env]) {
    if (opts.required) {
      console.error(`Unknown environment: ${env}`);
      console.error(`Valid environments: ${VALID_ENVS.join(', ')}`);
      process.exit(1);
    }
    return null;
  }

  // `--org <name|id>` (or CANARY_ORG) picks one of your logged-in sessions to
  // act as. It's an opt-in selector layered on top of the existing --env /
  // default resolution — a bare command with no --org behaves exactly as before.
  const orgSelector = getOrgSelector(argv, env);
  let selectedAuth: StoredAuth | null = null;
  if (orgSelector) {
    const match = await findSessionByOrg(orgSelector, env);
    if (match.kind === 'none') {
      if (opts.required) {
        printNoSessionForOrg(orgSelector, await readAllProfiles());
        process.exit(1);
      }
      return null;
    }
    if (match.kind === 'ambiguous') {
      if (opts.required) {
        printAmbiguousOrg(orgSelector, match.candidates);
        process.exit(1);
      }
      return null;
    }
    selectedAuth = match.auth;
  }

  let apiUrl: string;
  if (selectedAuth) {
    // The selected session's host is authoritative: never silently repoint an
    // org's bearer token to a different host via --api-url / CANARY_API_URL.
    apiUrl = selectedAuth.apiUrl ?? DEFAULT_API_URL;
    const explicitApiUrl = getArgValue(argv, '--api-url');
    if (explicitApiUrl && explicitApiUrl !== apiUrl) {
      if (opts.required) {
        console.error(`Error: the "${orgSelector}" session uses ${apiUrl}, but --api-url is ${explicitApiUrl}.`);
        console.error('Omit --api-url, or log that org in against the other host.');
        process.exit(1);
      }
      return null;
    }
  } else {
    const storedApiUrl = await readStoredApiUrl(profile);
    apiUrl =
      getArgValue(argv, '--api-url') ??
      (env ? ENV_URLS[env].api : undefined) ??
      readBrandEnv('CANARY_API_URL') ??
      storedApiUrl ??
      DEFAULT_API_URL;
  }

  const explicitToken = getArgValue(argv, '--token');

  let token: string | null;
  if (explicitToken) {
    token = explicitToken;
  } else if (selectedAuth) {
    // An --org / CANARY_ORG selection points at a specific stored session.
    token = selectedAuth.token ?? readBrandEnv('CANARY_API_TOKEN') ?? null;
    if (!token && opts.required) {
      console.error(`Error: No token stored for org "${orgSelector}".`);
      console.error(`Run: canary login --org "${orgSelector}"`);
      process.exit(1);
    }
  } else if (profile) {
    token = (await readStoredToken(profile)) ?? readBrandEnv('CANARY_API_TOKEN') ?? null;
    if (!token && opts.required) {
      console.error(`Error: No token found for "${env}" profile.`);
      console.error(`Run: canary login --env ${env}`);
      process.exit(1);
    }
  } else {
    token = readBrandEnv('CANARY_API_TOKEN') ?? (await readStoredToken()) ?? null;
    if (!token && opts.required) {
      console.error('Error: No API token found.');
      console.error('Run: canary login');
      process.exit(1);
    }
  }

  return token ? { apiUrl, token } : null;
}

/** Error output when `--org`/`CANARY_ORG` names an org with no stored session. */
export function printNoSessionForOrg(
  selector: string,
  all: { profiles: StoredProfile[] }
): void {
  console.error(`Error: No logged-in session for org "${selector}".`);
  const withOrg = all.profiles.filter((p) => p.auth.orgName || p.auth.orgId);
  if (withOrg.length > 0) {
    console.error('\nLogged-in orgs:');
    for (const { auth: a } of withOrg) {
      const envLabel = a.envLabel ? ` [${a.envLabel}]` : '';
      console.error(`  - ${a.orgName ?? '(unnamed)'} (${a.orgId ?? '?'})${envLabel}`);
    }
  }
  console.error(`\nTo add one:  canary login --org "${selector}"`);
  console.error(
    'Note: --org selects which of your orgs to act as. Superadmin commands that\n' +
      'target a different org use --target-org instead.'
  );
}

/** Error output when a selector matches more than one session. Distinguishes an
 * env split (same org in prod+dev → narrow with --env) from a genuine name clash
 * across distinct orgs (→ select by org ID, --env won't help). */
export function printAmbiguousOrg(selector: string, candidates: StoredProfile[]): void {
  const distinctOrgs = new Set(candidates.map((c) => c.auth.orgId ?? c.key));
  if (distinctOrgs.size <= 1) {
    console.error(`Error: Org "${selector}" is logged into multiple environments. Narrow with --env:`);
  } else {
    console.error(`Error: "${selector}" matches multiple orgs. Select by org ID:`);
  }
  for (const { auth: a } of candidates) {
    const envLabel = a.envLabel ?? '(unknown env)';
    console.error(`  - ${a.orgName ?? '(unnamed)'} (${a.orgId ?? '?'}) [${envLabel}]`);
  }
}

/**
 * Resolve API URL and auth token from argv flags, env vars, and stored profiles.
 * Used by knobs, feature-flag, release, and debug-session commands.
 */
export async function resolveConfig(argv: string[]): Promise<ResolvedConfig> {
  return (await resolveConfigInternal(argv, { required: true }))!;
}

export async function resolveOptionalConfig(argv: string[]): Promise<ResolvedConfig | null> {
  return resolveConfigInternal(argv, { required: false });
}

/**
 * Where the effective auth token would come from for this invocation:
 * - `flag`   — explicit `--token`
 * - `env`    — CANARY_API_TOKEN / COFACTOR_API_TOKEN
 * - `stored` — an interactive `canary login` session in auth.json
 * - `none`   — no token resolvable
 *
 * Mirrors the precedence in `resolveConfigInternal` exactly (which differs by
 * whether a profile is selected). `stored` is the only "logged in interactively"
 * source; `flag`/`env` are API-key style auth used by CI/automation.
 */
export async function resolveAuthSource(
  argv: string[]
): Promise<'flag' | 'env' | 'stored' | 'none'> {
  const env = getArgValue(argv, '--env');
  if (env && !ENV_URLS[env]) return 'none';
  const profile = env ? envToProfile(env) : undefined;

  if (getArgValue(argv, '--token')) return 'flag';

  const orgSelector = getOrgSelector(argv, env);
  if (orgSelector) {
    const match = await findSessionByOrg(orgSelector, env);
    if (match.kind === 'match' && match.auth.token) return 'stored';
    if (readBrandEnv('CANARY_API_TOKEN')) return 'env';
    return 'none';
  }

  if (profile) {
    // With a profile, stored auth wins over the env var.
    if (await readStoredToken(profile)) return 'stored';
    if (readBrandEnv('CANARY_API_TOKEN')) return 'env';
    return 'none';
  }

  // Without a profile, the env var wins over stored auth.
  if (readBrandEnv('CANARY_API_TOKEN')) return 'env';
  if (await readStoredToken()) return 'stored';
  return 'none';
}
